import QRCode from "qrcode";
import logoUrl from "@/assets/brand-logo.png";

export type QrStyle = { color: string; withLogo: boolean };

export const QR_COLORS: { value: string; label: string }[] = [
  { value: "#0B1F3A", label: "Azul profundo" },
  { value: "#7C3AED", label: "Morado" },
  { value: "#0F9D74", label: "Verde" },
  { value: "#111827", label: "Negro" },
];

export function loadImage(src: string, crossOrigin = false): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = src;
  });
}

/** Dibuja el QR (con corrección de errores alta para poder poner el logo al centro). */
export async function renderQrCanvas(text: string, style: QrStyle, size = 640): Promise<HTMLCanvasElement> {
  const canvas = document.createElement("canvas");
  await QRCode.toCanvas(canvas, text, {
    width: size,
    margin: 2,
    errorCorrectionLevel: "H",
    color: { dark: style.color, light: "#FFFFFF" },
  });
  if (style.withLogo) {
    try {
      const logo = await loadImage(logoUrl);
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const w = canvas.width;
        const r = w * 0.115;
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath();
        ctx.arc(w / 2, w / 2, r, 0, Math.PI * 2);
        ctx.fill();
        const box = r * 1.45;
        const ratio = logo.width / logo.height;
        const lw = ratio >= 1 ? box : box * ratio;
        const lh = ratio >= 1 ? box / ratio : box;
        ctx.drawImage(logo, w / 2 - lw / 2, w / 2 - lh / 2, lw, lh);
      }
    } catch {
      /* sin logo: el QR igual funciona */
    }
  }
  return canvas;
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Tarjeta digital en PNG (nombre, perfil, frase y QR). */
export async function renderCardPng(opts: {
  name: string;
  subtitle: string;
  tagline: string;
  link: string;
  avatarUrl: string | null;
  qr: QrStyle;
}): Promise<string> {
  const W = 800;
  const H = 1200;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no_canvas");

  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, "#0B1F3A");
  g.addColorStop(1, "#1B1446");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  ctx.textAlign = "center";
  ctx.fillStyle = "#CBD5E1";
  ctx.font = "600 30px 'Segoe UI', Arial, sans-serif";
  ctx.fillText("Tarjeta digital", W / 2, 90);

  // Avatar
  const cx = W / 2;
  const cy = 250;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, 90, 0, Math.PI * 2);
  ctx.clip();
  let drawn = false;
  if (opts.avatarUrl) {
    try {
      const img = await loadImage(opts.avatarUrl, true);
      const s = Math.max(180 / img.width, 180 / img.height);
      ctx.drawImage(img, cx - (img.width * s) / 2, cy - (img.height * s) / 2, img.width * s, img.height * s);
      drawn = true;
    } catch {
      drawn = false;
    }
  }
  if (!drawn) {
    ctx.fillStyle = "#7C3AED";
    ctx.fillRect(cx - 90, cy - 90, 180, 180);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "800 90px 'Segoe UI', Arial, sans-serif";
    ctx.fillText(opts.name.charAt(0).toUpperCase(), cx, cy + 32);
  }
  ctx.restore();

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "800 48px 'Segoe UI', Arial, sans-serif";
  ctx.fillText(opts.name, W / 2, 420);
  ctx.fillStyle = "#A5B4FC";
  ctx.font = "600 28px 'Segoe UI', Arial, sans-serif";
  ctx.fillText(opts.subtitle, W / 2, 465);
  ctx.fillStyle = "#E2E8F0";
  ctx.font = "400 26px 'Segoe UI', Arial, sans-serif";
  ctx.fillText(opts.tagline, W / 2, 530);

  const qr = await renderQrCanvas(opts.link, opts.qr, 520);
  ctx.fillStyle = "#FFFFFF";
  const bx = (W - 560) / 2;
  ctx.beginPath();
  ctx.roundRect(bx, 590, 560, 560 - 40, 28);
  ctx.fill();
  ctx.drawImage(qr, bx + 20, 590 + 20, 520, 480);

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "800 40px 'Segoe UI', Arial, sans-serif";
  ctx.fillText("VÍVELO AI", W / 2, 1180);
  return canvas.toDataURL("image/png");
}
